import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X, MapPin, Send, ChevronsRight } from "lucide-react";
import { Logo } from "./Logo";
import { CONTACTS } from "../data/services";

const LINKS = [
  { label: "Home", href: "#top" },
  { label: "Servizi", href: "#servizi" },
  { label: "Perché Noi", href: "#perche-noi" },
  { label: "Contatti", href: "#contatti" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -90, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="navbar"
    >
      <a
        href="#top"
        data-testid="navbar-logo-link"
        className="hidden md:flex absolute left-0 top-0 bottom-0 z-20 w-[300px] lg:w-[340px] bg-[#FFD500] flex-col justify-center pl-6 lg:pl-10"
        style={{ clipPath: "polygon(0 0, 100% 0, calc(100% - 44px) 100%, 0 100%)" }}
      >
        <span className="flex items-center gap-3 pr-14">
          <Logo size={46} />
          <span className="leading-none">
            <span className="font-display font-black text-3xl lg:text-4xl tracking-wide text-black block">PELUSO</span>
            <span className="font-mono-tac text-[10px] tracking-[0.4em] text-black/70 font-bold">SERVICE</span>
          </span>
        </span>
      </a>

      <div className="bg-[#0A0A0B] border-b border-white/10 hidden md:block relative z-10" data-testid="topbar">
        <div className="max-w-7xl mx-auto px-4 lg:px-10 md:pl-[320px] lg:pl-[380px] flex items-center justify-between">
          <div className="flex items-center gap-8 py-2.5">
            <span className="inline-flex items-center gap-2 font-mono-tac text-[11px] tracking-wider text-neutral-300 whitespace-nowrap" data-testid="topbar-address">
              <MapPin size={13} className="text-[#FFD500]" />
              Via Leonardo da Vinci 95, Z.I. Monterotondo (RM)
            </span>
            <a
              href={`mailto:${CONTACTS.email}`}
              className="hidden xl:inline-flex items-center gap-2 font-mono-tac text-[11px] tracking-wider text-neutral-300 hover:text-[#FFD500] transition-colors duration-300"
              data-testid="topbar-email"
            >
              <Send size={12} className="text-[#FFD500]" />
              {CONTACTS.email}
            </a>
          </div>
          <div className="flex items-center gap-5">
            <a
              href={CONTACTS.sede.href}
              aria-label="Chiama la sede"
              className="text-neutral-300 hover:text-[#FFD500] transition-colors duration-300"
              data-testid="topbar-phone-icon"
            >
              <Phone size={15} />
            </a>
            <span className="hidden xl:inline-flex items-center gap-2 font-mono-tac text-[10px] tracking-widest text-[#FFD500] whitespace-nowrap" data-testid="h24-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FFD500] animate-pulse" />
              H24 · 7/7
            </span>
            <a
              href="#modulo-richiesta"
              data-testid="topbar-contattaci"
              onClick={() => setTimeout(() => document.querySelector('[data-testid="contact-input-name"]')?.focus(), 700)}
              className="inline-flex items-center gap-1 bg-[#FFD500] text-black font-display font-bold uppercase tracking-wide text-sm px-6 py-2.5 -skew-x-12 hover:bg-white transition-colors duration-300"
            >
              <span className="skew-x-12 inline-flex items-center gap-1">
                Contattaci <ChevronsRight size={16} />
              </span>
            </a>
          </div>
        </div>
      </div>

      <div className="bg-white text-black shadow-lg shadow-black/20 relative" data-testid="mainbar">
        <div className="max-w-7xl mx-auto px-4 lg:px-10 md:pl-[320px] lg:pl-[380px] py-3 flex items-center justify-between">
          <span className="flex md:hidden items-center gap-3">
            <Logo size={44} />
            <span className="leading-none hidden sm:block">
              <span className="font-display font-black text-xl tracking-wide text-black block">PELUSO</span>
              <span className="font-mono-tac text-[10px] tracking-[0.35em] text-black/60 font-bold">SERVICE</span>
            </span>
          </span>

          <nav className="hidden md:flex items-center gap-8" data-testid="navbar-links">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-").replace("é", "e")}`}
                className="relative font-display font-bold uppercase tracking-wide text-base text-black/70 hover:text-black transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-[3px] after:w-0 after:bg-[#FFD500] after:transition-all after:duration-300 hover:after:w-full"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={CONTACTS.diretto.href}
              data-testid="navbar-call-button"
              className="hidden sm:inline-flex items-center gap-3 group"
            >
              <span className="w-10 h-10 rounded-full bg-[#FFD500] flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-12">
                <Phone size={17} strokeWidth={2.5} className="text-black" />
              </span>
              <span className="font-display font-black text-lg text-black tracking-wide group-hover:text-[#b89b00] transition-colors duration-300">
                +39 338 107 2423
              </span>
            </a>
            <button
              className="md:hidden text-black p-2"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-black/10 bg-white px-6 py-4 flex flex-col gap-4" data-testid="mobile-menu">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="font-display font-bold uppercase tracking-wide text-lg text-black/80 hover:text-black"
              >
                {l.label}
              </a>
            ))}
            <a href={CONTACTS.diretto.href} className="inline-flex items-center gap-2 font-display font-black uppercase text-black">
              <span className="w-8 h-8 rounded-full bg-[#FFD500] flex items-center justify-center">
                <Phone size={14} className="text-black" />
              </span>
              +39 338 107 2423
            </a>
          </div>
        )}
      </div>
    </motion.header>
  );
};
